import { Logout } from '@mui/icons-material'
import { Divider, Menu } from '@mui/material'
import { useContext, useEffect } from 'react'
import { UserRole } from '../../api/models'
import { AppStateContext } from '../../state/AppProvider'
import { getRoleLabel } from '../../utils/permissions'
import { ActionButton } from '../common/Button'
import { RequestAccessDialog } from '../dialogs/RequestAccessDialog'
import styles from './UserInfoMenu.module.css'
import { CustomTooltip } from '../CustomTooltip'

interface Props {
  anchorEl: HTMLElement | null
  open: boolean
  handleClose: () => void
  signOut: () => Promise<void>
  anchorOrigin?: {
    vertical: 'top' | 'bottom'
    horizontal: 'left' | 'right'
  }
  transformOrigin?: {
    vertical: 'top' | 'bottom'
    horizontal: 'left' | 'right'
  }
  className?: string
  menuId: string
  ariaLabelledBy: string
}
export const UserInfoMenu = ({
  anchorEl,
  open,
  handleClose,
  signOut,
  anchorOrigin,
  transformOrigin,
  className,
  menuId,
  ariaLabelledBy,
}: Props) => {
  const appStateContext = useContext(AppStateContext)
  const shouldShowExperimenterRequestButton = appStateContext?.state.user?.role === UserRole.VIEWER

  const handleOpenExperimenterRequestDialog = () => {
    appStateContext?.dispatch({ type: 'SET_IS_REQUEST_ACCESS_DIALOG_OPEN', payload: true })
  }

  useEffect(() => {
    const handleOpenAccessRequestDialog = () => {
      if (shouldShowExperimenterRequestButton && !appStateContext?.state.hasPendingRoleChangeRequest) {
        appStateContext?.dispatch({ type: 'SET_IS_REQUEST_ACCESS_DIALOG_OPEN', payload: true })
      } else if (appStateContext?.state.hasPendingRoleChangeRequest) {
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: {
            message: 'You already have a pending request. Please wait for it to be processed.',
            severity: 'warning',
          },
        })
      }
    }

    window.addEventListener('openAccessRequestDialog', handleOpenAccessRequestDialog)
    return () => {
      window.removeEventListener('openAccessRequestDialog', handleOpenAccessRequestDialog)
    }
  }, [shouldShowExperimenterRequestButton, appStateContext?.state.hasPendingRoleChangeRequest])

  return (
    <>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': ariaLabelledBy,
          },
        }}
        anchorOrigin={{
          vertical: anchorOrigin?.vertical || 'bottom',
          horizontal: anchorOrigin?.horizontal || 'right',
        }}
        transformOrigin={{
          vertical: transformOrigin?.vertical || 'top',
          horizontal: transformOrigin?.horizontal || 'right',
        }}
        className={className}>
        <div>
          <div className={styles.userInfoMenuSection}>
            <span className={styles.userInfoMenuInfoName}>{appStateContext?.state.user?.name}</span>
            <span className={styles.userInfoMenuInfoEmail}>{appStateContext?.state.user?.email}</span>
            <span className={styles.userInfoMenuInfoRole}>
              {getRoleLabel(appStateContext?.state.user?.role as UserRole)}
            </span>
            {shouldShowExperimenterRequestButton && (
              <CustomTooltip
                title="Request for Editor role"
                placement="right"
                classes={{
                  tooltip: styles.experimenterRequestButtonTooltip,
                }}>
                <span className={styles.experimenterRequestButtonTooltipSpan}>
                  <ActionButton
                    onClick={() => {
                      handleOpenExperimenterRequestDialog()
                    }}
                    template="secondary"
                    text={appStateContext?.state.hasPendingRoleChangeRequest ? 'Pending' : 'Editor Request'}
                    className={styles.experimenterRequestButton}
                    disabled={appStateContext?.state.hasPendingRoleChangeRequest}
                  />
                </span>
              </CustomTooltip>
            )}
          </div>
          <Divider className={styles.userInfoMenuDivider} />
          <div className={styles.userInfoMenuSection}>
            <div
              className={styles.userInfoMenuSignOut}
              onClick={signOut}>
              <Logout className={styles.userInfoMenuSignOutIcon} />
              <span className={styles.userInfoMenuSignOutText}>Sign Out</span>
            </div>
          </div>
        </div>
      </Menu>
      <RequestAccessDialog
        open={appStateContext?.state.isRequestAccessDialogOpen ?? false}
        onClose={() => appStateContext?.dispatch({ type: 'SET_IS_REQUEST_ACCESS_DIALOG_OPEN', payload: false })}
      />
    </>
  )
}
